import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { ElectionService } from 'src/app/services/election.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { Subject } from 'rxjs';
import { IShadowTableData } from 'src/app/interfaces/IShadowTableData';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';

@Component({
  selector: 'first-preference-by-place',
  templateUrl: './first-preference-by-place.component.html',
  styleUrls: ['./first-preference-by-place.component.scss'],
})
export class FirstPreferenceByPlaceComponent
  implements AfterViewInit, OnDestroy
{
  @Input() title = 'First preferences by voting centre & vote type';

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public columns: ITableHeader[] = [];
  public groupHeaders: Array<{
    column: string;
    label: string;
    colspan: number;
  }> = [];
  public candidateHeaders: Array<{
    column: string;
    label: string;
    labelTwo: string;
    colourCode: string | null;
    colspan: number;
  }> = [];
  public shadowTable: IShadowTableData[][] = [];
  public dataSource: any = null;
  public displayedColumns: string[] = [];
  public groupHeaderColumns: string[] = [];
  public candidateHeaderColumns: string[] = [];
  public pollingPlaceColumn = 'pollingPlace';
  /** Forces header/table re-creation when district changes so candidate names update. */
  public tableDistrictKey = '';

  constructor(
    public electionService: ElectionService,
    private cdr: ChangeDetectorRef
  ) {
    this.electionService.dataLoaded$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
          this.loadChart();
        }
      });
    this.electionService.pageChanged$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.loadChart();
      });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic,
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (
      this.isDataLoaded(
        this.electionService.selectedPage.electionDateString ?? ''
      )
    ) {
      this.loadChart();
    }
  }

  public customTrackBy(index: any, item: any) {
    return item.column;
  }

  /** TrackBy for candidate/group headers so DOM is recreated when district changes (fixes stale candidate names). */
  public trackByDistrictAndColumn(index: number, item: { column: string }): string {
    return (this.tableDistrictKey || '') + '_' + item.column;
  }

  private loadChart(): void {
    const page = this.electionService.selectedPage;
    if (page.pageType !== PageTypeEnum.CountHADistrict || !page.countDistrict) {
      this.tableDistrictKey = '';
      this.columns = [];
      this.groupHeaders = [];
      this.candidateHeaders = [];
      this.shadowTable = [];
      this.displayedColumns = [];
      this.groupHeaderColumns = [];
      this.candidateHeaderColumns = [];
      this.dataSource = new MatTableDataSource([]);
      this.cdr.markForCheck();
      return;
    }
    const election = this.electionService.electionMap.get(
      page.electionDateString ?? ''
    );
    this.columns = [];
    this.groupHeaders = [];
    this.candidateHeaders = [];
    this.shadowTable = [];
    this.displayedColumns = [];
    this.groupHeaderColumns = [];
    this.candidateHeaderColumns = [];
    this.dataSource = new MatTableDataSource([]);
    const foundDistrict = election?.electionCount?.getDistrict(page.countDistrict);
    if (foundDistrict) {
      this.tableDistrictKey = page.countDistrict ?? '';
      const numberOfCandidateColumns = foundDistrict.candidates.length * 2;

      this.columns.push({
        column: this.pollingPlaceColumn,
        label: 'Polling place',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 150,
        isBold: false,
        isRightJustify: false,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null,
      });

      this.groupHeaders = [
        { column: 'groupPlace', label: '', colspan: 1 },
        // {
        //   column: 'groupCandidates',
        //   label: 'Candidate results',
        //   colspan: numberOfCandidateColumns,
        // },
        // { column: 'groupTotals', label: 'Vote totals', colspan: 3 },
      ];
      this.groupHeaderColumns = this.groupHeaders.map((group) => group.column);

      this.candidateHeaders = [
        {
          column: 'candidatePlace',
          label: '',
          labelTwo: '',
          colourCode: null,
          colspan: 1,
        },
      ];

      foundDistrict.candidates.forEach((candidate, index) => {
        this.candidateHeaders.push({
          column: `candidate_${index}`,
          label: candidate.candidateName,
          labelTwo: candidate.partyId,
          colourCode: candidate.colourCode,
          colspan: 2,
        });

        this.columns.push({
          column: `c${index}_votes`,
          label: 'Votes',
          labelTwo: '',
          isTwoRowLabel: true,
          minWidth: 80,
          isBold: false,
          isRightJustify: true,
          isPercent: false,
          isColour: false,
          isColourBar: false,
          colourCode: null,
        });

        this.columns.push({
          column: `c${index}_percent`,
          label: '%',
          labelTwo: '',
          isTwoRowLabel: true,
          minWidth: 70,
          isBold: false,
          isRightJustify: true,
          isPercent: true,
          isColour: false,
          isColourBar: false,
          colourCode: null,
        });
      });

      this.candidateHeaders.push({
        column: 'candidateFormal',
        label: 'Formal votes',
        labelTwo: '',
        colourCode: null,
        colspan: 2,
      });
      this.candidateHeaders.push({
        column: 'candidateInformal',
        label: 'Informal votes',
        labelTwo: '',
        colourCode: null,
        colspan: 2,
      });
      this.candidateHeaders.push({
        column: 'candidateTotal',
        label: 'Total votes',
        labelTwo: '',
        colourCode: null,
        colspan: 2,
      });
      this.candidateHeaderColumns = this.candidateHeaders.map(
        (header) => header.column
      );

      this.columns.push({
        column: 'formal',
        label: 'Votes',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null,
      });
      this.columns.push({
        column: 'formal_pct',
        label: '%',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 70,
        isBold: false,
        isRightJustify: true,
        isPercent: true,
        isColour: false,
        isColourBar: false,
        colourCode: null,
      });
      this.columns.push({
        column: 'informal',
        label: 'Votes',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null,
      });
      this.columns.push({
        column: 'informal_pct',
        label: '%',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 70,
        isBold: false,
        isRightJustify: true,
        isPercent: true,
        isColour: false,
        isColourBar: false,
        colourCode: null,
      });
      this.columns.push({
        column: 'total',
        label: 'Votes',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 80,
        isBold: false,
        isRightJustify: true,
        isPercent: false,
        isColour: false,
        isColourBar: false,
        colourCode: null,
      });
      this.columns.push({
        column: 'total_pct',
        label: '%',
        labelTwo: '',
        isTwoRowLabel: true,
        minWidth: 70,
        isBold: false,
        isRightJustify: true,
        isPercent: true,
        isColour: false,
        isColourBar: false,
        colourCode: null,
      });

      const isPollingBooth = (pollingPlaceType: string): boolean => {
        return (
          pollingPlaceType === 'Polling Booth' ||
          pollingPlaceType === 'Host' ||
          pollingPlaceType === 'Visiting 1' ||
          pollingPlaceType === 'Visiting 2' ||
          pollingPlaceType === 'Mobile Poll Team' ||
          pollingPlaceType === 'Mobile Polling Team' ||
          pollingPlaceType === 'MT' ||
          pollingPlaceType === 'Shared Booth (Host)' ||
          pollingPlaceType === 'Shared Booth (Visitor 1)' ||
          pollingPlaceType === 'Shared Booth (Visitor 2)'
        );
      };

      const isEarlyVotingCentre = (pollingPlaceType: string): boolean => {
        return (
          pollingPlaceType === 'Early Voting Centre' ||
          pollingPlaceType === 'Early Voting' ||
          pollingPlaceType === 'Shared Early Voting Centre (Host)' || 
          pollingPlaceType === 'Shared Early Voting Centre (Visitor 1)' ||
          pollingPlaceType === 'Shared Early Voting Centre (Visitor 2)' ||
          pollingPlaceType === 'Shared Early Voting Centre (Parent)'
        );
      };

      const getCandidateVotesFromPollingPlace = (
        pollingPlace: any,
        candidateName: string
      ): number => {
        const pollingCandidate = pollingPlace.pollingCandidates.find(
          (pc: any) => pc.candidateName === candidateName
        );
        return pollingCandidate?.formalVotes || 0;
      };

      const createPollingPlaceRow = (
        pollingPlace: any,
        isSubtotal: boolean = false
      ): IShadowTableData[] => {
        const row: IShadowTableData[] = [];
        row.push({
          isNumber: false,
          numberData: 0,
          stringData: pollingPlace.pollingPlaceName || pollingPlace.label,
          isBold: isSubtotal,
        });

        foundDistrict.candidates.forEach((candidate, candidateIndex) => {
          const candidateVotes =
            pollingPlace.candidateVotes?.[candidateIndex] || 0;
          const totalFormal = pollingPlace.totalFormalVotes || 0;
          const percentage =
            totalFormal > 0 ? (candidateVotes / totalFormal) * 100 : 0;

          row.push({
            isNumber: true,
            numberData: candidateVotes,
            stringData: '',
            isBold: isSubtotal,
          });
          row.push({
            isNumber: true,
            numberData: percentage,
            stringData: '',
            isBold: isSubtotal,
          });
        });

        const formal = pollingPlace.totalFormalVotes || 0;
        const informal = pollingPlace.informalVotes || 0;
        const totalVotes = formal + informal;
        const formalPct =
          totalVotes > 0 ? (formal / totalVotes) * 100 : 0;
        const informalPct =
          totalVotes > 0 ? (informal / totalVotes) * 100 : 0;
        const totalPct = totalVotes > 0 ? 100 : 0;

        row.push({
          isNumber: true,
          numberData: formal,
          stringData: '',
          isBold: isSubtotal,
        });
        row.push({
          isNumber: true,
          numberData: formalPct,
          stringData: '',
          isBold: isSubtotal,
        });
        row.push({
          isNumber: true,
          numberData: informal,
          stringData: '',
          isBold: isSubtotal,
        });
        row.push({
          isNumber: true,
          numberData: informalPct,
          stringData: '',
          isBold: isSubtotal,
        });
        row.push({
          isNumber: true,
          numberData: totalVotes,
          stringData: '',
          isBold: isSubtotal,
        });
        row.push({
          isNumber: true,
          numberData: totalPct,
          stringData: '',
          isBold: isSubtotal,
        });

        return row;
      };

      let pollingBoothFormalTotal = 0;
      let pollingBoothInformalTotal = 0;
      let earlyVotingFormalTotal = 0;
      let earlyVotingInformalTotal = 0;
      const declarationFormalTotal =
        foundDistrict.districtFormalDeclarationVotes;
      const declarationInformalTotal =
        foundDistrict.districtInformalDeclarationVotes;
      const declarationCandidateVotes: number[] = foundDistrict.candidates.map(
        (c) => c.declarationVoteCount
      );

      const isMobilePollTeam = (pollingPlaceType: string): boolean =>
        pollingPlaceType === 'Mobile Poll Team' ||
        pollingPlaceType === 'Mobile Polling Team' ||
        pollingPlaceType === 'MT';

      const pollingBooths = foundDistrict.pollingPlaces
        .filter((pp) => isPollingBooth(pp.pollingPlaceType))
        .sort((a, b) => {
          const aMobile = isMobilePollTeam(a.pollingPlaceType);
          const bMobile = isMobilePollTeam(b.pollingPlaceType);
          if (aMobile !== bMobile) return aMobile ? 1 : -1;
          return a.pollingPlaceName.localeCompare(b.pollingPlaceName);
        });

      const earlyVotingCentres = foundDistrict.pollingPlaces
        .filter((pp) => isEarlyVotingCentre(pp.pollingPlaceType))
        .sort((a, b) => a.pollingPlaceName.localeCompare(b.pollingPlaceName));

      pollingBooths.forEach((pollingPlace) => {
        const candidateVotes = foundDistrict.candidates.map((candidate) => {
          return getCandidateVotesFromPollingPlace(
            pollingPlace,
            candidate.candidateName
          );
        });

        const rowData = {
          pollingPlaceName: pollingPlace.pollingPlaceName,
          candidateVotes,
          totalFormalVotes: pollingPlace.totalFormalVotes,
          informalVotes: pollingPlace.informalVotes,
        };

        this.shadowTable.push(createPollingPlaceRow(rowData));

        pollingBoothFormalTotal += pollingPlace.totalFormalVotes;
        pollingBoothInformalTotal += pollingPlace.informalVotes;
      });

      const pollingBoothCandidateTotals = foundDistrict.candidates.map(
        (candidate) => {
          return pollingBooths.reduce((sum, pp) => {
            return (
              sum +
              getCandidateVotesFromPollingPlace(pp, candidate.candidateName)
            );
          }, 0);
        }
      );

      const n = foundDistrict.candidates.length;
      const normalizeDeclarationType = (type: string): string =>
        (type || '').replace(/\s*-\s*Declaration\s*\d*\s*$/i, '').trim() || type || 'Declaration';
      const normalizeNameForMatch = (name: string): string =>
        (name || '').replace(/,/g, ' ').split(/\s+/).filter(Boolean).map((s) => s.trim().toLowerCase()).sort().join(' ');
      const getVoteForCandidate = (
        candidateVotes: { candidateId?: number | string; candidateFullName?: string; votes?: number; Votes?: number }[] | undefined,
        c: { candidateId: number; candidateName: string },
        candidateIndex?: number
      ): number => {
        if (!candidateVotes?.length) return 0;
        const idMatch = candidateVotes.find((v) => Number(v.candidateId) === Number(c.candidateId));
        if (idMatch != null) return idMatch.votes ?? (idMatch as any).Votes ?? 0;
        const exactName = candidateVotes.find((v) => String(v.candidateFullName || '').trim() === String(c.candidateName || '').trim());
        if (exactName != null) return exactName.votes ?? (exactName as any).Votes ?? 0;
        const normC = normalizeNameForMatch(c.candidateName);
        const nameMatch = candidateVotes.find((v) => normalizeNameForMatch(String(v.candidateFullName || '')) === normC);
        if (nameMatch != null) return nameMatch.votes ?? (nameMatch as any).Votes ?? 0;
        if (candidateIndex != null && candidateIndex >= 0 && candidateIndex < candidateVotes.length) {
          const byIndex = candidateVotes[candidateIndex];
          return byIndex != null ? (byIndex.votes ?? (byIndex as any).Votes ?? 0) : 0;
        }
        return 0;
      };
      const normalizeAbsentType = (type: string): string =>
        (type || '').replace(/\s*-\s*Absent\s+Declaration\s*\d*\s*$/i, '').replace(/\s*-\s*Declaration\s*\d*\s*$/i, '').trim() || type || 'Absent ordinary';

      const absentList = foundDistrict.absentOrdinary ?? [];
      const absentGroups = new Map<string, { formal: number; informal: number; candidateVotes: number[] }>();
      absentList.forEach((abs) => {
        const category = normalizeAbsentType(abs.absentOrdinaryType);
        const existing = absentGroups.get(category);
        const candidateVotes = foundDistrict.candidates.map((c, idx) => getVoteForCandidate(abs.candidateVotes, c, idx));
        if (existing) {
          existing.formal += abs.formalVotes ?? 0;
          existing.informal += abs.informalVotes ?? 0;
          existing.candidateVotes = existing.candidateVotes.map((v, idx) => v + (candidateVotes[idx] ?? 0));
        } else {
          absentGroups.set(category, {
            formal: abs.formalVotes ?? 0,
            informal: abs.informalVotes ?? 0,
            candidateVotes,
          });
        }
      });

      const getAbsentByCategory = (name: string) => {
        const key = Array.from(absentGroups.keys()).find((k) => k.toLowerCase() === name.toLowerCase());
        if (!key) return { formal: 0, informal: 0, candidateVotes: new Array(n).fill(0) as number[] };
        const g = absentGroups.get(key)!;
        const cv = g.candidateVotes.length >= n ? g.candidateVotes.slice(0, n) : [...g.candidateVotes, ...new Array(n - g.candidateVotes.length).fill(0)];
        return { formal: g.formal, informal: g.informal, candidateVotes: cv };
      };
      const pollDayAbsent = getAbsentByCategory('Polling Day');
      const earlyVotingAbsent = getAbsentByCategory('Early Voting');

      this.shadowTable.push(
        createPollingPlaceRow(
          {
            label: 'Polling day absent ordinary votes',
            candidateVotes: pollDayAbsent.candidateVotes,
            totalFormalVotes: pollDayAbsent.formal,
            informalVotes: pollDayAbsent.informal,
          },
          false
        )
      );
      this.shadowTable.push(
        createPollingPlaceRow(
          {
            label: 'Total polling booth ordinary votes',
            candidateVotes: Array.from({ length: n }, (_, i) => (pollingBoothCandidateTotals[i] ?? 0) + (pollDayAbsent.candidateVotes[i] ?? 0)),
            totalFormalVotes: pollingBoothFormalTotal + (pollDayAbsent.formal ?? 0),
            informalVotes: pollingBoothInformalTotal + (pollDayAbsent.informal ?? 0),
          },
          true
        )
      );

      const earlyVotingCandidateTotals: number[] = new Array(
        foundDistrict.candidates.length
      ).fill(0);
      earlyVotingCentres.forEach((pollingPlace) => {
        const candidateVotes = foundDistrict.candidates.map((candidate) => {
          return getCandidateVotesFromPollingPlace(
            pollingPlace,
            candidate.candidateName
          );
        });

        const rowData = {
          pollingPlaceName: pollingPlace.pollingPlaceName,
          candidateVotes,
          totalFormalVotes: pollingPlace.totalFormalVotes,
          informalVotes: pollingPlace.informalVotes,
        };

        this.shadowTable.push(createPollingPlaceRow(rowData));

        candidateVotes.forEach((votes, idx) => {
          earlyVotingCandidateTotals[idx] += votes;
        });
        earlyVotingFormalTotal += pollingPlace.totalFormalVotes;
        earlyVotingInformalTotal += pollingPlace.informalVotes;
      });

      this.shadowTable.push(
        createPollingPlaceRow(
          {
            label: 'Early voting absent ordinary votes',
            candidateVotes: earlyVotingAbsent.candidateVotes,
            totalFormalVotes: earlyVotingAbsent.formal,
            informalVotes: earlyVotingAbsent.informal,
          },
          false
        )
      );
      this.shadowTable.push(
        createPollingPlaceRow(
          {
            label: 'Total early voting centre ordinary votes',
            candidateVotes: Array.from({ length: n }, (_, i) => (earlyVotingCandidateTotals[i] ?? 0) + (earlyVotingAbsent.candidateVotes[i] ?? 0)),
            totalFormalVotes: earlyVotingFormalTotal + (earlyVotingAbsent.formal ?? 0),
            informalVotes: earlyVotingInformalTotal + (earlyVotingAbsent.informal ?? 0),
          },
          true
        )
      );

      const declarations = foundDistrict.declarations ?? [];
      const declarationGroups = new Map<string, { formal: number; informal: number; candidateVotes: number[] }>();
      declarations.forEach((decl) => {
        const category = normalizeDeclarationType(decl.declarationType);
        const existing = declarationGroups.get(category);
        const declVotesByCandidate = foundDistrict.candidates.map((c, idx) => getVoteForCandidate(decl.candidateVotes, c, idx));
        if (existing) {
          existing.formal += decl.formalVotes ?? 0;
          existing.informal += decl.informalVotes ?? 0;
          existing.candidateVotes = existing.candidateVotes.map((v, idx) => v + (declVotesByCandidate[idx] ?? 0));
        } else {
          declarationGroups.set(category, {
            formal: decl.formalVotes ?? 0,
            informal: decl.informalVotes ?? 0,
            candidateVotes: declVotesByCandidate,
          });
        }
      });
      const declarationDisplayOrder = [
        'Polling Day',
        'Early Voting',
        'Postal',
        'Electoral Visitor',
        'Mobile',
        'Telephone & Other',
        'Telephone',
        'Interstate',
        'Overseas'
      ];
      const getDeclarationSortIndex = (category: string): number => {
        const c = category.toLowerCase();
        const idx = declarationDisplayOrder.findIndex(
          (k) => c.includes(k.toLowerCase()) || k.toLowerCase().includes(c)
        );
        return idx >= 0 ? idx : 999;
      };
      const getDeclarationDisplayLabel = (category: string): string => {
        const c = category.toLowerCase();
        if (c.includes('polling day')) return 'Polling day declaration votes';
        if (c.includes('early voting')) return 'Early voting declaration votes';
        if (c.includes('postal')) return 'Postal votes';
        if (c.includes('electoral visitor') || c.includes('mobile')) return 'Electoral Visitor/ Mobile polling declaration votes';
        if (c.includes('telephone') || c.includes('interstate') || c.includes('overseas') || c.includes('other')) return 'Telephone/ Interstate/ Overseas declaration votes';
        return category.toLowerCase().includes('declaration') ? category : category + ' declaration';
      };
      const declarationCategoryOrder = Array.from(declarationGroups.keys()).sort((a, b) => {
        const idxA = getDeclarationSortIndex(a);
        const idxB = getDeclarationSortIndex(b);
        if (idxA !== idxB) return idxA - idxB;
        return a < b ? -1 : 1;
      });
      declarationCategoryOrder.forEach((category) => {
        const group = declarationGroups.get(category)!;
        this.shadowTable.push(
          createPollingPlaceRow(
            {
              label: getDeclarationDisplayLabel(category),
              candidateVotes: group.candidateVotes,
              totalFormalVotes: group.formal,
              informalVotes: group.informal,
            },
            false
          )
        );
      });

      this.shadowTable.push(
        createPollingPlaceRow(
          {
            label: 'Total declaration votes',
            candidateVotes: declarationCandidateVotes,
            totalFormalVotes: declarationFormalTotal,
            informalVotes: declarationInformalTotal,
          },
          true
        )
      );

      const allCandidateTotals = foundDistrict.candidates.map(
        (_, idx) =>
          (pollingBoothCandidateTotals[idx] ?? 0) +
          (earlyVotingCandidateTotals[idx] ?? 0) +
          (declarationCandidateVotes[idx] ?? 0) +
          (pollDayAbsent.candidateVotes[idx] ?? 0) +
          (earlyVotingAbsent.candidateVotes[idx] ?? 0)
      );
      const allFormalTotal =
        pollingBoothFormalTotal +
        earlyVotingFormalTotal +
        declarationFormalTotal +
        (pollDayAbsent.formal ?? 0) +
        (earlyVotingAbsent.formal ?? 0);
      const allInformalTotal =
        pollingBoothInformalTotal +
        earlyVotingInformalTotal +
        declarationInformalTotal +
        (pollDayAbsent.informal ?? 0) +
        (earlyVotingAbsent.informal ?? 0);

      this.shadowTable.push(
        createPollingPlaceRow(
          {
            label: 'Total all votes',
            candidateVotes: allCandidateTotals,
            totalFormalVotes: allFormalTotal,
            informalVotes: allInformalTotal,
          },
          true
        )
      );
    }

    const output = [];
    for (let i = 0; i < this.shadowTable.length; i++) {
      const newObj: { [k: string]: any } = {};
      for (let j = 0; j < this.columns.length; j++) {
        const tmpName: string = this.columns[j].column;
        const x = tmpName;
        const tmp = this.shadowTable[i][j].isNumber
          ? +this.shadowTable[i][j].numberData
          : this.shadowTable[i][j].stringData;
        newObj[tmpName] = tmp;
      }
      output.push(newObj);
    }

    this.displayedColumns = this.columns.map((c) => c.column);

    if (this.shadowTable?.length > 0) {
      this.dataSource = new MatTableDataSource(output);
    }
    this.cdr.detectChanges();
  }

  public isBold(data: string): boolean {
    return (
      data === 'Total polling booth ordinary votes' ||
      data === 'Total early voting centre ordinary votes' ||
      data === 'Total declaration votes' ||
      data === 'Total all votes'
    );
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
